/**
 * Ring extrusion pipeline for hd_neo.
 *
 * Simplified from hd_conf's extrudeDxf + extrudeRing pipeline:
 * - Single-segment rings only (no multi-material splitting)
 * - Default circular extrusion path (no custom DXF loop)
 * - No groove support
 * - No EdgeSplit modifier (uses computeVertexNormals directly)
 *
 * Scene scale: SCENE_SCALE = 0.2 → 1 mm = 0.2 Three.js units (1 unit ≈ 5 mm).
 * A size-54 ring (innerRadius ≈ 8.6 mm) renders at ~5.0 units outer diameter,
 * which fills ~50% of the hero viewport with the camera at Z=14.
 */

import {
    Box2,
    BufferGeometry,
    CatmullRomCurve3,
    MathUtils,
    Material,
    Mesh,
    NormalBufferAttributes,
    Shape,
    Vector2,
    Vector3,
} from 'three';
import DXFLoader from '../classes/DXFLoader';
import { EdgeSplitModifierMergeTolerance } from '../classes/EdgeSplitModifierMergeTolerance';
import ExtrudeGeometryUncapped from '../classes/ExtrudeGeometryUncapped';

/**
 * Edge-smoothing parameters matching hd_conf's staticConfig.sceneRingParams.
 * Edges below 30° are smoothed; sharper edges (e.g. sidecut corners) stay hard.
 */
const EDGE_SMOOTHING_ANGLE = 30; // degrees
const EDGE_SMOOTHING_MERGE_TOLERANCE = 0.00001;

/**
 * Scene unit scale: 1 mm → SCENE_SCALE Three.js units.
 * Applied to both DXF points and all mm measurements.
 */
export const SCENE_SCALE = 0.2;
const FILE_IMPORT_SCALE = SCENE_SCALE;
/** Spline interpolation quality (lower = faster, higher = smoother) */
const SPLINE_INTERPOLATION = 1;
/** Number of steps around the ring circumference */
const EXTRUSION_STEPS = 48;

export interface RingMeasurements {
    /** Ring circumference in mm (European size, e.g. 54) */
    size: number;
    /** Ring width in mm (e.g. 4) */
    width: number;
    /** Ring height in mm (e.g. 2) */
    height: number;
}

/**
 * Derives the inner radius in mm from a ring size (circumference in mm).
 */
export function innerRadiusFromSize(size: number): number {
    return size / (2 * Math.PI);
}

/**
 * Loads a DXF profile from a URL, processes it, and returns a Three.js Shape.
 */
export async function loadDxfShape(
    url: string,
    measurements: RingMeasurements,
): Promise<Shape> {
    const { width, height, size } = measurements;
    const innerRadius = innerRadiusFromSize(size);

    // Convert mm → Three.js scene units
    const innerRadiusM = innerRadius * SCENE_SCALE;
    const widthM = width * SCENE_SCALE;
    const heightM = height * SCENE_SCALE;

    const dxfLoader = new DXFLoader();
    dxfLoader.splineInterpolationPerSegment = SPLINE_INTERPOLATION;

    return new Promise<Shape>((resolve, reject) => {
        dxfLoader.load({
            url,
            options: { returnLinesAsPoints: true },
            onLoad: (dxfData) => {
                try {
                    // Flatten all point arrays into a single Vector2 array
                    const flatPoints: Vector2[] = [];
                    for (const arr of dxfData.points) {
                        for (const vec of arr) {
                            // dxfData.points contains Vector3; project to 2D
                            const v3 = vec as unknown as Vector3;
                            const pt = new Vector2(v3.x, v3.y);
                            // Rotate 180° around origin (DXF profiles face down)
                            pt.rotateAround(new Vector2(0, 0), MathUtils.degToRad(180));
                            // Scale: DXF mm → Three.js meters
                            pt.multiplyScalar(FILE_IMPORT_SCALE);
                            flatPoints.push(pt);
                        }
                    }

                    if (flatPoints.length === 0) {
                        reject(new Error('[loadDxfShape] DXF file contained no usable spline points'));
                        return;
                    }

                    // Remove duplicate points
                    const unique = removeDuplicates2D(flatPoints);

                    // Center the profile
                    const bb = new Box2().setFromPoints(unique);
                    const center = bb.getCenter(new Vector2());
                    const centered = unique.map((p) => p.clone().sub(center));

                    // Scale to fit inner radius
                    const bbCentered = new Box2().setFromPoints(centered);
                    const size2D = bbCentered.getSize(new Vector2());
                    const innerRadiusFraction = innerRadiusM / (size2D.y / 2);
                    const scaled = centered.map((p) => p.clone().multiplyScalar(innerRadiusFraction));

                    // Add closing point
                    const withClose = addClosingPoint2D(scaled);

                    // Scale to target width/height
                    const bbScaled = new Box2().setFromPoints(withClose);
                    const currentSize = bbScaled.getSize(new Vector2());
                    const scaleVec = new Vector2(
                        currentSize.x > 0 ? widthM / currentSize.x : 1,
                        currentSize.y > 0 ? heightM / currentSize.y : 1,
                    );
                    const finalPoints = withClose.map((p) => p.clone().multiply(scaleVec));

                    resolve(new Shape(finalPoints));
                } catch (err) {
                    reject(new Error(`[loadDxfShape] Processing failed: ${err}`));
                }
            },
            onError: (err) => {
                reject(new Error(`[loadDxfShape] Load failed: ${(err as unknown as Error).message}`));
            },
        });
    });
}

/**
 * Creates a default circular extrusion path for a ring.
 * Path runs along the centre line of the ring cross-section.
 */
export function createDefaultRingPath(measurements: RingMeasurements): CatmullRomCurve3 {
    const innerRadius = innerRadiusFromSize(measurements.size);
    const innerRadiusM = innerRadius * SCENE_SCALE;
    const heightM = measurements.height * SCENE_SCALE;
    // Path at centre of ring cross-section thickness
    const coreRadius = innerRadiusM + heightM / 2;

    const points: Vector3[] = [];
    for (let i = 0; i < EXTRUSION_STEPS; i++) {
        const theta = (i / EXTRUSION_STEPS) * 2 * Math.PI;
        points.push(new Vector3(coreRadius * Math.cos(theta), coreRadius * Math.sin(theta), 0));
    }

    // Start from the furthest positive-X point
    const maxXIdx = points.reduce((maxI, p, i) => (p.x > points[maxI].x ? i : maxI), 0);
    const ordered = [...points.slice(maxXIdx), ...points.slice(0, maxXIdx)];

    const curve = new CatmullRomCurve3(ordered, true, 'centripetal');
    curve.arcLengthDivisions = 300;
    curve.updateArcLengths();
    return curve;
}

/**
 * Extrudes a profile Shape along a circular path and returns a Three.js Mesh.
 */
export function extrudeRingShape(
    profileShape: Shape,
    ringPath: CatmullRomCurve3,
    material: Material,
): Mesh<BufferGeometry<NormalBufferAttributes>, Material> {
    const geometry = new ExtrudeGeometryUncapped(profileShape, {
        curveSegments: 12,
        steps: EXTRUSION_STEPS,
        bevelEnabled: false,
        extrudePath: ringPath,
        extrudePathIsClosed: true,
        extrudePathGetUnspacedPoints: true,
        computeVertexNormals: false,
        copyLastToFirst: true,
        deleteInnerFaces: false,
    });

    // Apply edge-split modifier (same strategy as hd_conf's extrudeRing):
    // Merges vertices within tolerance, then splits edges that exceed the cutoff
    // angle so curved surfaces are smooth while hard corners stay sharp.
    const edgeSplitter = new EdgeSplitModifierMergeTolerance();
    const modifiedGeo = edgeSplitter.modify(
        geometry,
        MathUtils.degToRad(EDGE_SMOOTHING_ANGLE),
        false,
        EDGE_SMOOTHING_MERGE_TOLERANCE,
    );
    modifiedGeo.computeVertexNormals();
    return new Mesh(modifiedGeo, material);
}

/**
 * Full pipeline: loads DXF, builds ring path, extrudes, returns mesh.
 * Rejects if DXF URL is unavailable or parsing fails.
 */
export async function buildRingFromDxf(
    dxfUrl: string,
    measurements: RingMeasurements,
    material: Material,
): Promise<Mesh> {
    const shape = await loadDxfShape(dxfUrl, measurements);
    const ringPath = createDefaultRingPath(measurements);
    return extrudeRingShape(shape, ringPath, material);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function removeDuplicates2D(points: Vector2[]): Vector2[] {
    if (points.length <= 1) return points;
    const seen = new Set<string>();
    return points.filter((p) => {
        const key = `${p.x},${p.y}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function addClosingPoint2D(points: Vector2[]): Vector2[] {
    if (points.length === 0) return points;
    if (!points[0].equals(points[points.length - 1])) {
        return [...points, points[0].clone()];
    }
    return points;
}
